import React from "react";
import { useAuth } from "../../context/AuthContext";
import { useLegalAcceptance } from "../../context/LegalAcceptanceContext";
import LegalAcceptanceModal from "./LegalAcceptanceModal";
import "./LegalAcceptance.css";

function GateShell({ title, body, onRetry, onSignOut }) {
  return (
    <main className="legal-gate-screen" aria-busy="false">
      <section className="legal-gate-panel" aria-labelledby="legal-gate-title">
        <h1 id="legal-gate-title">{title}</h1>
        <p>{body}</p>
        <div className="legal-gate-actions">
          {onRetry ? (
            <button type="button" className="legal-secondary-button" onClick={onRetry}>
              Retry
            </button>
          ) : null}
          {onSignOut ? (
            <button type="button" className="legal-text-button" onClick={onSignOut}>
              Sign out
            </button>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function OperationalError({ onRetry, onSignOut }) {
  return (
    <GateShell
      title="Flowtrak is temporarily unavailable"
      body="We could not verify your account requirements. Try again or sign out."
      onRetry={onRetry}
      onSignOut={onSignOut}
    />
  );
}

export default function LegalAcceptanceGate({ children }) {
  const auth = useAuth();
  const legal = useLegalAcceptance();

  const hydrated = !!auth?.hydrated;
  const isAuthenticated = !!auth?.isAuthenticated;
  const signOut = auth?.signOut || auth?.clearSession;

  if (!hydrated || !isAuthenticated) return <>{children}</>;
  if (!legal.uiEnabled) return <>{children}</>;

  if (legal.phase === "idle" || legal.phase === "loading") {
    return (
      <main className="legal-gate-screen" aria-busy="true">
        <section className="legal-gate-panel" aria-labelledby="legal-loading-title">
          <h1 id="legal-loading-title">Preparing Flowtrak</h1>
          <p>Checking your account requirements.</p>
        </section>
      </main>
    );
  }

  if (legal.phase === "error" || legal.statusError) {
    return <OperationalError onRetry={legal.refreshStatus} onSignOut={signOut} />;
  }

  const status = legal.status;
  if (!status) {
    return <OperationalError onRetry={legal.refreshStatus} onSignOut={signOut} />;
  }
  if (status.enforcementEnabled === false) return <>{children}</>;
  if (status.requirementsSatisfied) return <>{children}</>;
  if (status.isImpersonating) return <>{children}</>;

  if (status.wouldRequireAcceptance && !status.configurationReady) {
    return (
      <GateShell
        title="Flowtrak is temporarily unavailable"
        body="Account requirements are not ready yet. Try again or sign out."
        onRetry={legal.refreshStatus}
        onSignOut={signOut}
      />
    );
  }

  if (status.mustAcceptNow) {
    return <LegalAcceptanceModal />;
  }

  return <OperationalError onRetry={legal.refreshStatus} onSignOut={signOut} />;
}
